<template>
  <div class="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
        {{ i18n.t('message.registerCaption') }}
      </h2>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
        <form class="space-y-6" @submit.prevent="handleSubmit">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700">
              {{ i18n.t('message.emailCaption') }}
            </label>
            <div class="mt-1">
              <input
                id="email"
                v-model="email"
                type="email"
                required
                :class="['appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm',
                  emailError ? 'border-red-500' : 'border-gray-300']"
              />
              <p v-if="emailError" class="mt-1 text-sm text-red-600">{{ emailError }}</p>
            </div>
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-gray-700">
              {{ i18n.t('message.passwordCaption') }}
            </label>
            <div class="mt-1">
              <input
                id="password"
                v-model="password"
                type="password"
                required
                :class="['appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm',
                  passwordError ? 'border-red-500' : 'border-gray-300']"
              />
              <p v-if="passwordError" class="mt-1 text-sm text-red-600">{{ passwordError }}</p>
              <div class="mt-6">
                <button
                  type="submit"
                  :disabled="!isRegistrationValid || isLoading"
                  class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  <template v-if="isLoading">
                    <svg class="animate-spin -ml-1 mr-2 h-4 w-4 inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ i18n.t('message.processing') }}
                  </template>
                  <template v-else>
                    {{ i18n.t('message.completeRegistration') }}
                  </template>
                </button>
              </div>
            </div>
          </div>

          <!-- VK Login Button -->
          <div class="mt-6">
            <div class="relative">
              <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-gray-300"></div>
              </div>
              <div class="relative flex justify-center text-sm">
                <span class="px-2 bg-white text-gray-500"> {{ i18n.t('message.or') }} </span>
              </div>
            </div>
          </div>
          <div ref="vkIdContainerRef" id="vk-id-onetap-container" class="mt-3 flex justify-center"></div>


        </form>

        <div v-if="errors.length" class="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">{{ i18n.t('message.errorTitle') }}</h3>
              <div class="mt-2 text-sm text-red-700">
                <ul class="list-disc pl-5 space-y-1">
                  <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-6">
          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-300" />
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-white text-gray-500">
                {{ i18n.t('message.alreadyregisteredCaption') }}
              </span>
            </div>
          </div>
          <div class="mt-6 text-center">
            <router-link
              to="/signin"
              class="font-medium text-indigo-600 hover:text-indigo-500"
            >
              {{ i18n.t('message.signinCaption') }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import axiosInstance from '@/axios/axios';
import { useAuthStore } from '@/stores/user';
import { useI18n } from 'vue-i18n';
import * as VKID from '@vkid/sdk';

const i18n = useI18n();

const VITE_VK_CLIENT_ID = import.meta.env.VITE_VK_CLIENT_ID;

let isVkSdkConfigInitialized = false;

const vkIdContainerRef = ref(null);
let vkOneTapInstance = null;

const email = ref('');
const password = ref('');
const isLoading = ref(false);
const errors = ref([]);
const emailError = ref('');
const passwordError = ref('');

const router = useRouter();
const authStore = useAuthStore();

const isRegistrationValid = computed(() => {
  return (
    email.value &&
    password.value &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value) &&
    password.value.length >= 6
  );
});

const initializeVkOneTap = async () => {
  if (!vkIdContainerRef.value) {
    await nextTick();
    if (!vkIdContainerRef.value) {
      return;
    }
  }

  if (typeof window.VKIDSDK === 'undefined') {
    console.error('VK SDK (VKIDSDK) not loaded.');
    errors.value.push(i18n.t('message.vkSdkLoadError', 'VK login service is unavailable. Please refresh.'));
    return;
  }

  if (!VITE_VK_CLIENT_ID || VITE_VK_CLIENT_ID.includes('YOUR_') || VITE_VK_CLIENT_ID === 'YOUR_VK_APP_ID_PLACEHOLDER') {
    console.error('VK Client ID (VITE_VK_CLIENT_ID) is not configured correctly.');
    errors.value.push(i18n.t('message.vkConfigError', 'VK login is misconfigured. Contact support.'));
    return;
  }

  if (!isVkSdkConfigInitialized) {
    try {
      VKID.Config.init({
        app: parseInt(VITE_VK_CLIENT_ID, 10),
        redirectUrl: `${window.location.origin}/oauth/vk/callback`,
        state: 'state',
        source: VKID.ConfigSource.LOWCODE,
        codeVerifier: 'codeVerifier',
        scope: 'email phone',
      });
      isVkSdkConfigInitialized = true;
    } catch (configError) {
      console.error('Error initializing VK SDK Config:', configError);
      errors.value.push(i18n.t('message.vkSdkInitError', 'Failed to initialize VK login service.'));
      return;
    }
  }

  if (vkOneTapInstance && typeof vkOneTapInstance.destroy === 'function') {
    vkOneTapInstance.destroy();
  }

  vkOneTapInstance = new VKID.OneTap();

  vkOneTapInstance
    .render({
      container: vkIdContainerRef.value,
      scheme: VKID.Scheme.LIGHT,
      showAlternativeLogin: true,
    })
    .on(VKID.WidgetEvents.ERROR, handleVkSdkGlobalError)
    .on(VKID.OneTapInternalEvents.LOGIN_SUCCESS, (payload) => {
      isLoading.value = true;
      errors.value = [];
      const { code, device_id } = payload;
      VKID.Auth.exchangeCode(code, device_id)
        .then(handleVkLoginSuccess)
        .catch(handleVkSdkGlobalError);
    });
};

const handleVkLoginSuccess = async (vkAuthData) => {
  const tokenForBackend = vkAuthData.access_token || vkAuthData.token;
  const vkUser = vkAuthData.user || {
    id: vkAuthData.user_id,
    email: vkAuthData.email,
    first_name: vkAuthData.first_name,
    last_name: vkAuthData.last_name,
  };

  if (!tokenForBackend) {
    errors.value.push(i18n.t('message.vkTokenError', 'VK authentication successful, but token missing.'));
    isLoading.value = false;
    return;
  }

  try {
    const response = await axiosInstance.post('/api/auth/vk-login/', {
      access_token: tokenForBackend,
      email: vkUser.email,
      vk_user_id: vkUser.id,
      first_name: vkUser.first_name,
      last_name: vkUser.last_name,
    });

    if (response.data && response.data.access_token && response.data.user) {
      authStore.setTokens(response.data.access_token, response.data.refresh_token);
      authStore.setUser(response.data.user);
      router.push('/app');
    } else {
      errors.value.push(i18n.t('message.vkLoginBackendError', 'Login after VK auth failed on our server.'));
    }
  } catch (error) {
    console.error('Error during backend VK login processing:', error);
    const errorMessage =
      error.response?.data?.detail ||
      error.response?.data?.message ||
      i18n.t('message.vkLoginError', 'An error occurred with VK login.');
    if (!errors.value.includes(errorMessage)) errors.value.push(errorMessage);
  } finally {
    isLoading.value = false;
  }
};

const handleVkSdkGlobalError = (error) => {
  console.error('VK SDK Error:', error);
  let errorMessage = i18n.t('message.vkGenericError', 'VK login failed.');
  if (error) {
    if (typeof error === 'string') errorMessage = error;
    else if (error.description) errorMessage = `VK: ${error.description}`;
    else if (error.error_description) errorMessage = `VK: ${error.error_description}`;
    else if (error.message) errorMessage = `VK: ${error.message}`;
    else if (error.type) errorMessage = `VK Error: ${error.type}`;
  }
  if (!errors.value.includes(errorMessage)) errors.value.push(errorMessage);
  isLoading.value = false;
};

const handleSubmit = async () => {
  errors.value = [];
  emailError.value = '';
  passwordError.value = '';

  if (!isRegistrationValid.value) {
    if (!email.value) {
      emailError.value = i18n.t('message.emailRequired', 'Email is required.');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
      emailError.value = i18n.t('message.emailInvalid', 'Please enter a valid email address.');
    }
    if (!password.value) {
      passwordError.value = i18n.t('message.passwordRequired', 'Password is required.');
    }
    return;
  }

  isLoading.value = true;

  try {
    const registerData = {
      username: email.value,
      email: email.value,
      password: password.value,
    };

    const { data: registerResponse } = await axiosInstance.post('/api/auth/register/', registerData);

    if (registerResponse) {
      const loginSuccess = await authStore.login({
        username: email.value,
        password: password.value,
      });

      if (loginSuccess) {
        router.push('/app');
      } else {
        errors.value.push(
          i18n.t('message.loginAfterRegistrationFailed', 'Registration successful, but auto-login failed. Please sign in.')
        );
        router.push('/signin');
      }
    } else {
      errors.value.push(i18n.t('message.registrationFailedUnknown', 'Registration failed for an unknown reason.'));
    }
  } catch (error) {
    console.error('Registration or Login error:', error);
    if (error.response && error.response.data) {
      const errorData = error.response.data;
      if (errorData.email) emailError.value = Array.isArray(errorData.email) ? errorData.email.join(' ') : errorData.email;
      if (errorData.password)
        passwordError.value = Array.isArray(errorData.password) ? errorData.password.join(' ') : errorData.password;
      const generalError =
        errorData.detail || errorData.message || (Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors.join(' ') : null);
      if (generalError && !errors.value.includes(generalError)) errors.value.push(generalError);
    }
    if (!errors.value.length && !emailError.value && !passwordError.value) {
      errors.value.push(i18n.t('message.registrationError', 'An error occurred during registration.'));
    }
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await nextTick();
  if (vkIdContainerRef.value) initializeVkOneTap();
});

onBeforeUnmount(() => {
  if (vkOneTapInstance && typeof vkOneTapInstance.destroy === 'function') {
    try {
      vkOneTapInstance.destroy();
    } catch (e) {
      console.error('Error destroying VK OneTap instance:', e);
    }
  }
  vkOneTapInstance = null;
});
</script>
